import { useQuery } from '@apollo/client';
import { useCallback, useEffect, useState } from 'react';

import { MAX_NUMBER_OF_NOTES, NOTES_MAX_RESULTS } from '../config/constants';
import { GET_NOTES_BY_DROPOFFID } from '../services/graphql/notes/notesQuery';

const useDropoffNotes = ({ dropoffId }: { dropoffId: string }) => {
  const {
    loading,
    fetchMore,
    error: errorNotes,
    data,
    refetch,
    updateQuery,
  } = useQuery(GET_NOTES_BY_DROPOFFID, {
    variables: {
      dropoffId,
      offset: 0,
      limit: NOTES_MAX_RESULTS,
    },
  });

  const [notes, setNotes] = useState(data?.getNotesByDropoffId.data ?? []);

  const [totalNotes, setTotalNotes] = useState(
    data?.getNotesByDropoffId.count || 0,
  );

  const [hasNextPage, setHasNextPage] = useState<boolean>(true);

  function calculateHasNextPage(
    total: number,
    limit: number,
    currentTotal: number,
  ) {
    const currentPage = Math.ceil(currentTotal / limit);
    const totalPages = Math.ceil(total / limit);

    return currentPage < totalPages;
  }

  function refetchNotes() {
    setHasNextPage(true);
    setNotes([]);

    refetch({
      dropoffId,
      offset: 0,
      limit: NOTES_MAX_RESULTS,
    });
  }

  useEffect(() => {
    if (
      data?.getNotesByDropoffId.data &&
      data?.getNotesByDropoffId.data.length > 0
    ) {
      setNotes(data.getNotesByDropoffId.data);
      setTotalNotes(data.getNotesByDropoffId.count);
    }
  }, [data]);

  const fetchMoreNotes = useCallback(() => {
    if (notes.length === MAX_NUMBER_OF_NOTES) return;

    fetchMore({
      variables: {
        dropoffId,
        offset: notes.length,
        limit: NOTES_MAX_RESULTS,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;

        const newData = fetchMoreResult.getNotesByDropoffId.data || [];
        setNotes((prevNotes: any) => [...prevNotes, ...newData]);

        // Verificar si hay más páginas después de la página actual
        setHasNextPage(
          calculateHasNextPage(
            fetchMoreResult.getNotesByDropoffId.count || 0,
            NOTES_MAX_RESULTS,
            notes.length + newData.length,
          ),
        );

        return {
          getNotesByDropoffId: {
            ...prev.getNotesByDropoffId,
            data: [...prev.getNotesByDropoffId.data, ...newData],
          },
        };
      },
    });
  }, [fetchMore, notes, dropoffId]);

  return {
    fetchMoreNotes,
    loading,
    notes,
    refetchNotes,
    errorNotes,
    hasNextPage,
    updateQuery,
    totalNotes,
  };
};

export default useDropoffNotes;
